import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Phone, MapPin, Film, Wrench, Smartphone, Shield, Cookie, FileText, BookOpen, Mail, Accessibility, ShieldAlert } from 'lucide-react';
import { csupaszUtvonal } from '../constants/ctaMentesOldalak';
import { trackCallClick, trackOutbound } from '../utils/analytics';
import { BUSINESS, OPERATORS, STORES, storeAddressDisplay } from '../constants/stores';
import { isBoltPath, boltHref } from '../utils/boltMod';

// Custom social icons
const FacebookIcon = ({ size = 18, className = "" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TikTokIcon = ({ size = 18, className = "" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const InstagramIcon = ({ size = 18, className = "" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const Footer = () => {
  const { pathname } = useLocation();
  const currentYear = new Date().getFullYear();
  // Bolt mód (/bolt/...): szerviz-mentes lábléc – utils/boltMod.js
  const bolt = isBoltPath(pathname);
  const boltSzur = (lista) => (bolt
    ? lista.map((l) => ({ ...l, href: boltHref(l.href) })).filter((l) => l.href && !l.external)
    : lista);

  // Üzletadatok KIZÁRÓLAG a központi configból (Sprint1 Menet1) – kézzel írt
  // cím/telefon/nyitvatartás itt nem lehet.
  const stores = [
    {
      id: STORES.belvaros.id,
      name: 'NorbiPhone Belváros',
      address: storeAddressDisplay(STORES.belvaros),
      phone: STORES.belvaros.phoneDisplay,
      facebook: STORES.belvaros.facebook,
    },
    {
      id: STORES.tesco.id,
      name: 'NorbiPhone Tesco',
      address: storeAddressDisplay(STORES.tesco),
      phone: STORES.tesco.phoneDisplay,
      facebook: STORES.tesco.facebook,
    }
  ];

  const quickLinks = boltSzur([
    { href: '/', label: 'Kezdőlap', icon: Smartphone },
    { href: '/hidrogel/', label: 'Hidrogél Fólia', icon: Film },
    { href: '/iphone-kijelzocsere-oroshaza/', label: 'Kijelzőcsere', icon: Wrench },
    { href: '/telefonok/', label: 'Telefonok', icon: Smartphone },
    { href: '/felvasarlas/', label: 'Felvásárlás', icon: MapPin },
    { href: '/uzletek/', label: 'Üzleteink', icon: MapPin },
    // A /diagnosztika/ önálló STATIKUS oldal (frontend/public/diagnosztika/),
    // NEM React route → `external: true`, sima <a>. <Link>-kel a router a
    // NotFoundPage-re vinne. Külső auditon nulla belső hivatkozás mutatott rá.
    { href: '/diagnosztika/', label: 'Telefon bevizsgálás', icon: Smartphone, external: true },
    // Ellopott/elveszett készülék bejelentése. A láblécben a helye: aki keresi,
    // az célzottan keresi – a főmenüben viszont elvenné a helyet a vásárlási
    // útvonalaktól, és nem is napi funkció.
    { href: '/bejelentes/', label: 'Ellopott telefon bejelentése', icon: ShieldAlert },
    { href: '/blog/',    label: 'Blog',       icon: BookOpen },
    { href: '/gyakori-kerdesek/', label: 'GYIK', icon: BookOpen },
    { href: '/miert-norbiphone/', label: 'Miért NorbiPhone?', icon: Shield }
  ]);

  const legalLinks = boltSzur([
    { href: '/adatkezeles/', label: 'Adatkezelési tájékoztató', icon: Shield },
    { href: '/cookie/', label: 'Cookie tájékoztató', icon: Cookie },
    { href: '/aszf/', label: 'ÁSZF', icon: FileText },
    // A kaparós sorsjegy szabályzata: a nyerési esélyek attól nyilvánosak, hogy
    // MINDENHONNAN elérhetők, nem csak a jegy melletti panelből.
    { href: '/vip-jatekszabalyzat/', label: 'VIP játékszabályzat', icon: FileText },
    { href: '/akadalymentesseg/', label: 'Akadálymentesség', icon: Accessibility },
    { href: '/impresszum/', label: 'Impresszum', icon: FileText }
  ]);

  // Csupasz oldalon (pl. /nyitva) nincs nagy lábléc – az oldal sajátot visz.
  if (csupaszUtvonal(pathname)) return null;

  return (
    <footer className="bg-ink text-white pb-[calc(64px+env(safe-area-inset-bottom))] sm:pb-0">
      {/* TikTok Banner */}
      <div className="bg-brand py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <a
            href="https://www.tiktok.com/@norbiphone"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackOutbound('https://www.tiktok.com/@norbiphone', 'footer')}
            className="flex items-center justify-center gap-3 text-white font-semibold hover:opacity-90 transition-opacity"
            data-testid="footer-tiktok-banner"
          >
            <TikTokIcon size={24} />
            <span>Kövess minket a TikTokon is!</span>
          </a>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            {/* width/height: a `w-auto` miatt a böngésző a betöltésig nem tudná,
                milyen széles lesz a logó → a mellette lévő tartalom ugrálna (CLS).
                A .webp veszteségmentes (SSIM 1.0), csak kisebb: 97,0 → 52,6 kB. */}
            <picture>
              <source srcSet="/logo-full.webp" type="image/webp" />
              <img
                src="/logo-full.png"
                alt="NorbiPhone"
                data-testid="footer-logo"
                className="h-28 w-auto mb-6"
                width="298"
                height="500"
                loading="lazy"
                decoding="async"
              />
            </picture>
            <p className="text-white/70 mb-6 leading-relaxed text-sm">
              {bolt
                ? 'Használt telefonok, készülékfelvásárlás és tartozékok Orosházán, két üzletben.'
                : 'Mobiltelefon-szerviz és tartozékbolt Orosházán, két üzletben. iPhone, Samsung, Xiaomi, Honor és Huawei készülékek javítása garanciával.'}
            </p>
            
            {/* E-mail kapcsolat (Sprint1 Menet1) */}
            <a
              href={`mailto:${BUSINESS.email}`}
              className="flex items-center gap-2 text-white/70 hover:text-brand transition-colors text-sm mb-6"
              data-testid="footer-email"
            >
              <Mail size={16} className="text-brand" />
              {BUSINESS.email}
            </a>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/NorbiPhone"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutbound('https://www.facebook.com/NorbiPhone', 'footer')}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand transition-colors"
                data-testid="footer-facebook-belvaros"
                aria-label="NorbiPhone Facebook"
              >
                <FacebookIcon size={18} />
              </a>
              <a
                href="https://www.tiktok.com/@norbiphone"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutbound('https://www.tiktok.com/@norbiphone', 'footer')}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand transition-colors"
                data-testid="footer-tiktok"
                aria-label="NorbiPhone TikTok"
              >
                <TikTokIcon size={18} />
              </a>
              <a
                href="https://www.instagram.com/norbiphone"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutbound('https://www.instagram.com/norbiphone', 'footer')}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand transition-colors"
                data-testid="footer-instagram"
                aria-label="NorbiPhone Instagram"
              >
                <InstagramIcon size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="font-bold text-lg mb-6">{bolt ? 'Oldalak' : 'Szolgáltatások'}</h2>
            <ul className="space-y-3">
              {quickLinks.map((link) => {
                const testId = `footer-quick-link-${link.href.replace(/\//g, '') || 'home'}`;
                const cls = 'flex items-center gap-2 text-white/70 hover:text-brand transition-colors text-sm';
                return (
                  <li key={link.href}>
                    {link.external ? (
                      <a href={link.href} data-testid={testId} className={cls}>
                        <link.icon size={16} />
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} data-testid={testId} className={cls}>
                        <link.icon size={16} />
                        {link.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Store 1 - Belváros */}
          <div>
            <h2 className="font-bold text-lg mb-6">NorbiPhone Belváros</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-white/70">
                <MapPin size={16} className="text-brand flex-shrink-0 mt-0.5" />
                <span>{stores[0].address}</span>
              </li>
              <li>
                <a
                  href={`tel:${stores[0].phone.replace(/\s/g, '')}`}
                  onClick={() => trackCallClick('footer')}
                  className="flex items-center gap-2 text-[#39B54A] hover:underline"
                  data-testid="footer-phone-belvaros"
                >
                  <Phone size={16} />
                  {stores[0].phone}
                </a>
              </li>
              <li>
                <a
                  href={stores[0].facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/70 hover:text-brand transition-colors"
                  data-testid="footer-facebook-link-belvaros"
                >
                  <FacebookIcon size={16} />
                  Facebook oldal
                </a>
              </li>
            </ul>
          </div>

          {/* Store 2 - Tesco */}
          <div>
            <h2 className="font-bold text-lg mb-6">NorbiPhone Tesco</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-white/70">
                <MapPin size={16} className="text-brand flex-shrink-0 mt-0.5" />
                <span>{stores[1].address}</span>
              </li>
              <li>
                <a
                  href={`tel:${stores[1].phone.replace(/\s/g, '')}`}
                  onClick={() => trackCallClick('footer')}
                  className="flex items-center gap-2 text-[#39B54A] hover:underline"
                  data-testid="footer-phone-tesco"
                >
                  <Phone size={16} />
                  {stores[1].phone}
                </a>
              </li>
              <li>
                <a
                  href={stores[1].facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/70 hover:text-brand transition-colors"
                  data-testid="footer-facebook-link-tesco"
                >
                  <FacebookIcon size={16} />
                  Facebook oldal
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-white/10 mt-10 pt-8">
          <div className="flex flex-wrap items-center justify-center gap-6">
            {legalLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`footer-legal-link-${link.href.replace(/\//g, '') || 'home'}`}
                className="flex items-center gap-2 text-white/50 hover:text-brand transition-colors text-sm"
              >
                <link.icon size={14} />
                {link.label}
              </Link>
            ))}
          </div>
          {/* Cégadat-blokk (Ekertv.): a két üzletet két külön egyéni vállalkozó
              üzemelteti – mindkettő feltüntetve; a részletek az Impresszumban.
              Az adatok a stores.js OPERATORS-ból jönnek (TODO-k ott töltendők ki). */}
          <p className="mt-5 text-center text-white/40 text-xs leading-relaxed" data-testid="footer-operators">
            Üzemeltetők:{' '}
            {OPERATORS.map((op, i) => (
              <span key={op.storeKey}>
                {i > 0 && ' · '}
                {op.storeLabel}: {op.name}
              </span>
            ))}
            {' '}– részletes adatok az <Link to="/impresszum/" className="underline hover:text-brand">Impresszumban</Link>.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              © 2026 NorbiPhone. Minden jog fenntartva.
              <span className="block text-white/60 text-xs mt-1">{bolt ? 'NorbiPhone – Orosháza' : 'NorbiPhone – Mobiltelefon szerviz Orosháza'}</span>
            </p>
            <p className="flex items-center gap-1 text-white/50 text-sm">
              <Heart size={14} className="text-brand" /> Szeretettel készült Orosházán
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
